import React, {useContext, useMemo, useRef} from "react";
import {useTheme} from "styled-components";
import {useMessages, type Messages} from "i18n";
import {RollingCountdown} from "countdown/RollingCountdown";
import {GameDialog} from "games/GameDialog";
import {PlayGameButton} from "games/GameOffer";
import {FINISHED_TRANSFER, runHasFinish} from "games/gameProgress";
import type {TransferGame} from "games/useTransferGame";
import {Mode, planOf} from "home/gating";
import {PeerActivityListener, Unsubscribe} from "core/p2p/peerActivity";
import {ToastContext} from "Toast";
import {useIsLoggedIn, useUser} from "UserProvider";
import {useLoginModal} from "login/LoginModalProvider";
import EmailComponent from "home/EmailComponent";
import {InstantLiveCard} from "./InstantLiveCard";
import {ClockIcon, DownArrowIcon, LockIcon, MailIcon} from "./icons";
import {ShareActionsRow} from "./ShareActionsRow";
import {ShareLinkBox} from "./ShareLinkBox";
import {useEmailInstead} from "./useEmailInstead";
import {useScrollGutter} from "./useScrollGutter";
import {DeliveryNotice, deliveryNoticeMessage} from "core/transfer/deliveryOutcome";
import {
	classifySenderFailure,
	reportSenderFailure,
	SenderFailureContext,
	senderFailureText,
} from "home/senderFailures";
import {
	Achip, ActionsRow, BtnOut, buildConfetti, Chips, Compose, Conf, ConfLayer, confettiColors,
	ExpiryLine, ExpiryPanel, ExpiryValue, Sharing, QuietButton, Root, SentNote, SubmitButton, SuccessScroll,
	SuccessSurface, Title,
} from "./SuccessCard.styles";

/**
 * Link sharing comes first; optional page settings and factual expiry stay in separate panels. The share row
 * directly under the link ("Show QR code", and the game's Play control when this transfer offers one, both opening
 * a dialog) and the personalization offer directly under it are the live card's own (kitecorp/transferify-ui#3333,
 * #3388).
 */

interface Props {
	personaliseAction?: React.ReactNode;
	mode: Mode;
	url: string;
	/** Anchored once when success begins; null is the indefinite retention. */
	expiresAtMs: number | null;
	/** Whether download protection is enabled; the raw password is never retained after submission. */
	passwordProtected: boolean;
	/** The download cap the sender set (if any) — renders the "N downloads max" active-option chip. */
	maxDownloads?: number;
	/** Advisory prior-recipient baseline from either direct addresses or selected group snapshots. */
	initialRecipientEmails?: string[];
	/**
	 * Who the upload that produced this screen actually reached (ui#369). Absent for a send that
	 * notified nobody; a later "Email instead" send replaces it with its own outcome.
	 */
	deliveryNotice?: DeliveryNotice;
	/**
	 * The hosted transfer's game slot, the one its upload had (`useUploadGame`, kitecorp/transferify-ui#3388), so
	 * the card offers the game the upload picked. The Instant live card holds its own.
	 */
	hostedGame: TransferGame;
	onSendAnother: () => void;
	/** TransferService.onPeerActivity, threaded by CreateFlow (instant mode only). */
	subscribePeerActivity: (listener: PeerActivityListener) => Unsubscribe;
}

interface OptionChip {
	key: string;
	icon: React.ReactNode;
	label: string;
}

/** The enabled PRO options, summarized as chips (design `.chips`): password lock + download cap. */
function activeOptionChips(success: Messages["sender"]["success"], passwordProtected: boolean,
						   maxDownloads?: number): OptionChip[] {
	const chips: OptionChip[] = [];
	if (passwordProtected) {
		chips.push({key: "password", icon: <LockIcon size={13}/>, label: success.passwordProtected});
	}
	if (maxDownloads !== undefined && maxDownloads > 0) {
		chips.push({
			key: "limit",
			icon: <DownArrowIcon size={12}/>,
			label: success.downloadsMax(maxDownloads),
		});
	}
	return chips;
}

interface EmailInsteadFieldsProps {
	onToEmails: (emails: string[]) => void;
}

/**
	 * Reuses the create-flow {@link EmailComponent} recipient chips and per-plan cap. Sender identity
	 * comes from the authenticated account, so the post-upload action hides the sender field.
	 */
function EmailInsteadFields({onToEmails}: EmailInsteadFieldsProps) {
	const [user] = useUser();
	const {sender} = useMessages();
	return (
		<EmailComponent
			recipientLabel={sender.emailInstead.sendTo}
			plan={planOf(user)}
			onEmailsTo={onToEmails}
			onEmailFrom={() => undefined}
			showSender={false}
			onUpgrade={() => {}}
			onError={() => {}}
		/>
	);
}

function ServerSuccess({
	personaliseAction,
	url,
	expiresAtMs,
	passwordProtected,
	maxDownloads,
	initialRecipientEmails = [],
	deliveryNotice,
	hostedGame: game,
	onSendAnother,
}: Omit<Props, "subscribePeerActivity">) {
	const {sender, games} = useMessages();
	const [user] = useUser();
	const isAuthenticated = useIsLoggedIn();
	const {open: openLogin} = useLoginModal();
	const {tokens} = useTheme();
	const confetti = useMemo(() => buildConfetti(confettiColors(tokens)), [tokens]);
	const chips = activeOptionChips(sender.success, passwordProtected, maxDownloads);
	const email = useEmailInstead(url, planOf(user), initialRecipientEmails, isAuthenticated, openLogin);
	const {showSuccessToast, showWarningToast, showErrorToast} = useContext(ToastContext);
	// A post-hoc "Email instead" send supersedes the upload's own outcome — it is the newer answer to
	// the same question, and it is the send the reader just triggered.
	const notice = email.notice ?? deliveryNotice ?? null;
	const skipped = notice === null ? null : deliveryNoticeMessage(notice, sender.success.delivery);

	const onSendEmail = async () => {
		try {
			const sent = await email.send();
			if (sent === null) {
				return;
			}
			const sentSkipped = deliveryNoticeMessage(sent, sender.success.delivery);
			if (sentSkipped === null) {
				showSuccessToast(sender.success.emailedToast);
			} else {
				showWarningToast(sentSkipped);
			}
		} catch (e) {
			const failure = classifySenderFailure(e, SenderFailureContext.Email);
			reportSenderFailure(SenderFailureContext.Email, failure);
			showErrorToast(senderFailureText(failure, sender.errors, sender.deliveryRetry.retryAfter));
		}
	};

	return (
		<Root>
			<ConfLayer aria-hidden="true">
				{confetti.map(piece => (
					<Conf
						key={piece.id}
						style={{
							left: `${piece.left}%`,
							width: `${piece.w}px`,
							height: `${piece.h}px`,
							background: piece.color,
							animationDelay: `${piece.delay}s`,
							animationDuration: `${piece.dur}s`,
						}}
					/>
				))}
			</ConfLayer>
			<Sharing>
				<Title>{sender.success.title}</Title>
				<ShareLinkBox url={url}/>
				<ShareActionsRow url={url} play={game.offersPlay && game.game !== null
					? <PlayGameButton game={game.game} onPlay={game.play} compact/>
					: null}/>
				{/*
				  * The upload is over, so a round opened here races nothing: it has no finish line, as on the live
				  * card after its first recipient's download, and the line under a lost round says the link is ready.
				  */}
				{game.game !== null && (
					<GameDialog
						open={game.open}
						game={game.game}
						mode="upload"
						progress={FINISHED_TRANSFER}
						endless={!runHasFinish(FINISHED_TRANSFER)}
						statusLine={games.common.linkReady}
						onClose={game.close}
						onContinue={game.close}
						onRunFinished={game.runFinished}
						onRunClosed={game.runClosed}
					/>
				)}
			</Sharing>
			{personaliseAction}
			<ExpiryPanel aria-label={sender.success.expiryHeading}>
				<ExpiryLine><ClockIcon size={15}/>{sender.success.expiryHeading}</ExpiryLine>
				<ExpiryValue>{expiresAtMs === null
					? sender.expiryNever
					: <RollingCountdown expiresAtMs={expiresAtMs}/>}</ExpiryValue>
				{chips.length > 0 && (
					<Chips>
						{chips.map(chip => (
							<Achip key={chip.key}>{chip.icon}{chip.label}</Achip>
						))}
					</Chips>
				)}
			</ExpiryPanel>
			{email.phase === "compose" ? (
				<Compose>
					<EmailInsteadFields onToEmails={email.setToEmails}/>
					<ActionsRow>
						<BtnOut type="button" onClick={email.cancel} disabled={email.sending}>{sender.success.back}</BtnOut>
						<SubmitButton type="button" onClick={onSendEmail} disabled={!email.canSend}>
							{email.sending ? sender.success.sending : sender.success.sendEmail}
						</SubmitButton>
					</ActionsRow>
				</Compose>
			) : (
				<>
					{/*
					  * The skipped-recipient line outlives its toast on purpose: "your recipient never
					  * got the file" is the one outcome here a 3s Snackbar must not be the only telling
					  * of. It is a live region because it can appear without any focus change, when the
					  * upload's own delivery is what skipped someone.
					  */}
					{skipped !== null ? (
						<SentNote $warn role="status" aria-live="polite">
							<MailIcon size={14}/>{skipped}
						</SentNote>
					) : email.phase === "sent" && (
						<SentNote role="status" aria-live="polite">
							<MailIcon size={14}/>{sender.success.linkEmailed}
						</SentNote>
					)}
					<ActionsRow>
						<QuietButton type="button" onClick={email.open}>
							<MailIcon size={15}/>{sender.success.emailInstead}
						</QuietButton>
						<SubmitButton type="button" onClick={onSendAnother}>{sender.sendAnother}</SubmitButton>
					</ActionsRow>
				</>
			)}
		</Root>
	);
}

export function SuccessCard({
	personaliseAction,
	mode,
	url,
	expiresAtMs,
	passwordProtected,
	maxDownloads,
	initialRecipientEmails,
	deliveryNotice,
	hostedGame,
	onSendAnother,
	subscribePeerActivity,
}: Props) {
	const scroll = useRef<HTMLDivElement>(null);
	useScrollGutter(scroll, "--success-scroll-gutter");
	// The link a send produces is ALWAYS live now (ui#1408). It used to be conditional: a transfer could
	// be created with its personalization unpaid, and the api then withheld the link from every recipient
	// path until the sender came back to pay or release it — so this screen led with a hold panel instead
	// of a share link, and asked the api which of the two it was on a poll. The owner removed that whole
	// fallback: a branded look needs no payment, because every plan includes it
	// (kitecorp/transferify#525), and dismissing the composer sends unpersonalized, so there is no
	// unpaid-but-sent state left to ask about.
	//
	// ui#1653 wraps both cards in one box with one scroller (the held height it also gave the box went
	// with ui#1969).
	//
	// SuccessGiftFrame owns the single offer placement below either success card.
	return (
		<SuccessSurface data-testid="success-card">
			<SuccessScroll ref={scroll}>
				{mode === "instant"
					? (
						<InstantLiveCard
							url={url}
							personaliseAction={personaliseAction}
							onSendAnother={onSendAnother}
							subscribeActivity={subscribePeerActivity}
						/>
					)
					: (
						<ServerSuccess
							personaliseAction={personaliseAction}
							mode={mode}
							url={url}
							expiresAtMs={expiresAtMs}
							passwordProtected={passwordProtected}
							maxDownloads={maxDownloads}
							initialRecipientEmails={initialRecipientEmails}
							deliveryNotice={deliveryNotice}
							hostedGame={hostedGame}
							onSendAnother={onSendAnother}
						/>
					)}
			</SuccessScroll>
		</SuccessSurface>
	);
}
